import * as React from 'react';

import {
  Button,
  DialogContent,
  DialogHeader,
  DialogOverlay,
  TextArea,
} from '@rematter/ui-web';
import { CheckCircle } from 'lucide-react';

import { useTranslations } from '~/modules/trade/i18n/useTranslations';

interface ClosePurchaseOrderDialogProps {
  purchaseOrderId: string;
  onClose: (purchaseOrderId: string, notes?: string) => Promise<void>;
  onCancel?: () => void;
}

export const ClosePurchaseOrderDialog: React.FC<
  ClosePurchaseOrderDialogProps
> = ({ purchaseOrderId, onClose, onCancel }) => {
  const t = useTranslations();
  const [isLoading, setIsLoading] = React.useState(false);
  const [notes, setNotes] = React.useState('');

  const handleConfirm = async () => {
    try {
      setIsLoading(true);
      await onClose(purchaseOrderId, notes || undefined);
    } catch (error) {
      console.error('Error closing purchase order:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <DialogOverlay isOpen className="@ClosePurchaseOrderDialog">
      <DialogContent>
        <DialogHeader>
          <div className="text-base font-semibold">
            {t.closePurchaseOrderDialog.title}
          </div>
        </DialogHeader>
        <div className="flex flex-col gap-4 px-4">
          <div className="text-base text-neutral/50">
            {t.closePurchaseOrderDialog.message}
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-neutral/70">
              {t.closePurchaseOrderDialog.notesLabel}
            </label>
            <TextArea
              value={notes}
              onChange={setNotes}
              placeholder={t.closePurchaseOrderDialog.notesPlaceholder}
              rows={3}
            />
          </div>
          <div className="flex justify-end gap-2 py-2">
            <Button variant="text" color="neutral" onPress={onCancel}>
              {t.closePurchaseOrderDialog.cancel}
            </Button>
            <Button
              variant="solid"
              color="primary"
              onPress={handleConfirm}
              icon={<CheckCircle className="size-4" />}
              isDisabled={isLoading}
            >
              {t.closePurchaseOrderDialog.confirm}
            </Button>
          </div>
        </div>
      </DialogContent>
    </DialogOverlay>
  );
};
